import React, { useEffect, useState, useCallback } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Zap, RefreshCw, TrendingUp, Users, Truck, Calendar, Scale, CheckCircle2,
} from 'lucide-react';
import { formatCurrency, formatNumber } from '../utils/format';

/**
 * Instant Reports — powered by the Projection Engine.
 *
 *  All five reports read from pre-aggregated snapshots so each tab loads
 *  in ~20-50 ms regardless of how many journals exist. The page also
 *  exposes an admin "Rebuild" button that wipes the snapshots and
 *  re-applies every journal — useful after manual data corrections.
 */
const TABS = [
  { key: 'trial',    label: 'Trial Balance',    icon: Scale,    test: 'tab-trial' },
  { key: 'projects', label: 'Project Profit',   icon: TrendingUp, test: 'tab-projects' },
  { key: 'cust',     label: 'Customer Balances', icon: Users,   test: 'tab-customers' },
  { key: 'supp',     label: 'Supplier Balances', icon: Truck,   test: 'tab-suppliers' },
  { key: 'pnl',      label: 'Monthly P&L',      icon: Calendar, test: 'tab-pnl' },
];

const InstantReports = ({ user }) => {
  const isAdmin = (user?.role || JSON.parse(localStorage.getItem('user') || '{}').role || '').toLowerCase() === 'admin';
  const [active, setActive] = useState('trial');
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [latency, setLatency] = useState({});
  const [rebuilding, setRebuilding] = useState(false);

  const endpoint = {
    trial:    '/projections/trial-balance',
    projects: '/projections/project-profit',
    cust:     '/projections/customer-balances',
    supp:     '/projections/supplier-balances',
    pnl:      '/projections/monthly-pnl',
  };

  const fetchTab = useCallback(async (tab) => {
    setLoading(true);
    const t0 = performance.now();
    try {
      const { data: payload } = await axiosInstance.get(endpoint[tab]);
      setData((d) => ({ ...d, [tab]: payload }));
      setLatency((l) => ({ ...l, [tab]: Math.round(performance.now() - t0) }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load report');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchTab(active); }, [active, fetchTab]);

  const rebuild = async () => {
    if (!window.confirm('Rebuild ALL projections from scratch?\nThis is safe — it reads journals only.')) return;
    setRebuilding(true);
    try {
      const { data: res } = await axiosInstance.post('/projections/rebuild');
      toast.success(`Rebuilt — ${res.journals_processed} journals processed`);
      fetchTab(active);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Rebuild failed');
    } finally {
      setRebuilding(false);
    }
  };

  return (
    <div data-testid="instant-reports-page" className="space-y-4">
      <div className="page-header">
        <h2 className="page-title flex items-center gap-2">
          <Zap className="w-6 h-6 text-amber-500" /> Instant Reports
          <span className="text-xs font-normal text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full ml-2 inline-flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" /> Projection Engine
          </span>
        </h2>
        <div className="flex gap-2">
          <button onClick={() => fetchTab(active)} disabled={loading} className="btn-secondary inline-flex items-center gap-2">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
          {isAdmin && (
            <button
              data-testid="rebuild-projections"
              onClick={rebuild}
              disabled={rebuilding}
              className="btn-primary inline-flex items-center gap-2"
            >
              <Zap className="w-4 h-4" /> {rebuilding ? 'Rebuilding…' : 'Rebuild All'}
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => {
          const Icon = t.icon;
          const isActive = active === t.key;
          return (
            <button
              key={t.key}
              data-testid={t.test}
              onClick={() => setActive(t.key)}
              className={`px-4 py-2 rounded-lg font-semibold text-sm inline-flex items-center gap-2 transition ${
                isActive
                  ? 'bg-purple-600 text-white shadow-md'
                  : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Latency badge */}
      {latency[active] != null && (
        <p className="text-xs text-gray-500">
          Loaded from projection snapshot in <strong className="text-emerald-700">{latency[active]} ms</strong>
        </p>
      )}

      {/* Report bodies */}
      <div className="bg-white rounded-xl shadow border border-gray-200 p-4">
        {active === 'trial'    && <TrialBalanceTable    data={data.trial}    loading={loading} />}
        {active === 'projects' && <ProjectProfitTable   data={data.projects} loading={loading} />}
        {active === 'cust'     && <PartyBalancesTable   data={data.cust}     loading={loading} kind="customer" />}
        {active === 'supp'     && <PartyBalancesTable   data={data.supp}     loading={loading} kind="supplier" />}
        {active === 'pnl'      && <MonthlyPnLTable      data={data.pnl}      loading={loading} />}
      </div>
    </div>
  );
};

// ── tables ─────────────────────────────────────────────────────────────────
const Empty = ({ text = 'No data yet.' }) => <p className="text-sm text-gray-500 text-center py-6">{text}</p>;
const Loading = () => <p className="text-sm text-gray-400 text-center py-6">Loading…</p>;

const TrialBalanceTable = ({ data, loading }) => {
  if (loading || !data) return <Loading />;
  if (!data.accounts?.length) return <Empty text="No accounts have activity yet. Try Rebuild." />;
  return (
    <>
      <div className="flex flex-wrap gap-3 mb-4 text-sm">
        <Pill label="Total Debit"  value={formatCurrency(data.total_debit)}  color="text-blue-700" />
        <Pill label="Total Credit" value={formatCurrency(data.total_credit)} color="text-emerald-700" />
        <Pill label={data.balanced ? 'Balanced ✓' : '⚠ Unbalanced'}
              value="" color={data.balanced ? 'text-emerald-700' : 'text-red-700'} />
      </div>
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
          <tr>
            <th className="px-3 py-2">Code</th>
            <th className="px-3 py-2">Account</th>
            <th className="px-3 py-2 text-right">Debit</th>
            <th className="px-3 py-2 text-right">Credit</th>
          </tr>
        </thead>
        <tbody>
          {data.accounts.map((a) => (
            <tr key={a.account_id} className="border-t" data-testid={`tb-row-${a.account_id}`}>
              <td className="px-3 py-2 font-mono text-xs">{a.account_number || a.system_code || '-'}</td>
              <td className="px-3 py-2 font-semibold">{a.account_name}</td>
              <td className="px-3 py-2 text-right">{a.debit_balance ? formatCurrency(a.debit_balance) : '—'}</td>
              <td className="px-3 py-2 text-right">{a.credit_balance ? formatCurrency(a.credit_balance) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

const ProjectProfitTable = ({ data, loading }) => {
  if (loading || !data) return <Loading />;
  if (!data.projects?.length) return <Empty text="No project P&L yet." />;
  return (
    <table className="w-full text-sm">
      <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
        <tr>
          <th className="px-3 py-2">Project</th>
          <th className="px-3 py-2 text-right">Contract Value</th>
          <th className="px-3 py-2 text-right">Revenue</th>
          <th className="px-3 py-2 text-right">Cost</th>
          <th className="px-3 py-2 text-right">Profit</th>
          <th className="px-3 py-2 text-right">Margin</th>
        </tr>
      </thead>
      <tbody>
        {data.projects.map((p) => (
          <tr key={p.project_id} className="border-t" data-testid={`pp-row-${p.project_id}`}>
            <td className="px-3 py-2 font-semibold">{p.project_name || p.project_id}</td>
            <td className="px-3 py-2 text-right text-gray-500">{formatCurrency(p.contract_value || 0)}</td>
            <td className="px-3 py-2 text-right text-blue-700">{formatCurrency(p.revenue || 0)}</td>
            <td className="px-3 py-2 text-right text-rose-700">{formatCurrency(p.cost || 0)}</td>
            <td className={`px-3 py-2 text-right font-bold ${(p.profit || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
              {formatCurrency(p.profit || 0)}
            </td>
            <td className="px-3 py-2 text-right">{formatNumber(p.margin_pct || 0, { maximumFractionDigits: 1 })}%</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const PartyBalancesTable = ({ data, loading, kind }) => {
  if (loading || !data) return <Loading />;
  const rows = data[kind === 'customer' ? 'customers' : 'suppliers'];
  if (!rows?.length) return <Empty text={`No ${kind} balances yet.`} />;
  return (
    <table className="w-full text-sm">
      <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
        <tr>
          <th className="px-3 py-2">{kind === 'customer' ? 'Customer' : 'Supplier'}</th>
          <th className="px-3 py-2 text-right">Outstanding Balance</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const id = r.customer_id || r.supplier_id;
          const name = r.customer_name || r.supplier_name || id;
          return (
            <tr key={id} className="border-t">
              <td className="px-3 py-2 font-semibold">{name}</td>
              <td className={`px-3 py-2 text-right font-bold ${r.balance > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                {formatCurrency(r.balance || 0)}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

const MonthlyPnLTable = ({ data, loading }) => {
  if (loading || !data) return <Loading />;
  if (!data.months?.length) return <Empty text="No monthly activity yet." />;
  return (
    <table className="w-full text-sm">
      <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
        <tr>
          <th className="px-3 py-2">Period</th>
          <th className="px-3 py-2 text-right">Revenue</th>
          <th className="px-3 py-2 text-right">Cost of Projects</th>
          <th className="px-3 py-2 text-right">Gross Profit</th>
          <th className="px-3 py-2 text-right">Admin Expenses</th>
          <th className="px-3 py-2 text-right">Net Profit</th>
        </tr>
      </thead>
      <tbody>
        {data.months.map((m) => (
          <tr key={m.period} className="border-t">
            <td className="px-3 py-2 font-mono font-bold">{m.period}</td>
            <td className="px-3 py-2 text-right text-blue-700">{formatCurrency(m.revenue || 0)}</td>
            <td className="px-3 py-2 text-right text-rose-700">{formatCurrency(m.cost_of_projects || 0)}</td>
            <td className="px-3 py-2 text-right">{formatCurrency(m.gross_profit || 0)}</td>
            <td className="px-3 py-2 text-right text-orange-700">{formatCurrency(m.admin_expenses || 0)}</td>
            <td className={`px-3 py-2 text-right font-bold ${m.net_profit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
              {formatCurrency(m.net_profit || 0)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const Pill = ({ label, value, color }) => (
  <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border bg-gray-50 ${color}`}>
    <span className="text-xs text-gray-500">{label}</span>
    {value && <span className="font-semibold">{value}</span>}
  </span>
);

export default InstantReports;
